<template>
  <PaginaAdministrar
    titulo="Contabilistas"
    subtitulo="Contabilistas responsáveis (registro 0100 do SPED)"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-5">
          <label class="form-label">Buscar por nome, CPF ou CRC</label>
          <input class="form-control" placeholder="Ex: João..." v-model="filtros.busca" @keyup.enter="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatus" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="s in listas.status" :key="s.idListaSistemaItem" :value="s.idListaSistemaItem">{{ s.textoLista }}</option>
          </select>
        </div>
        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">
      <i class="bi bi-exclamation-triangle-fill me-2"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="contabilistas"
      :loading="loading"
      empty-message="Nenhum contabilista encontrado."
      row-key="idContabilista"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="emit('editar-item', $event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idContabilista }}</span>
      </template>
      <template #crc="{ row }">
        <span v-if="row.crcNumero">{{ row.crcNumero }}<span v-if="row.crcUf">/{{ row.crcUf }}</span></span>
        <span v-else class="text-muted">-</span>
      </template>
      <template #localidade="{ row }">
        <span v-if="row.cidade || row.uf">{{ row.cidade }}<span v-if="row.uf">/{{ row.uf }}</span></span>
        <span v-else class="text-muted">-</span>
      </template>
      <template #status="{ row }">
        <span class="badge" :class="obterClasseStatusGhost(getStatusTexto(row.lstStatus))">
          {{ getStatusTexto(row.lstStatus) }}
        </span>
      </template>
      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper">
          <button class="btn-icon" title="Ver Histórico de Alterações" @click="abrirAuditoria(row)">
            <i class="bi bi-clock-history text-secondary"></i>
          </button>
          <button class="btn-icon text-primary" @click="emit('editar-item', row)" title="Editar">
            <i class="bi bi-pencil-fill"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="Contabilista"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    :dicionarios="{ 'lstStatus': listas.status }"
    @fechar="mostrarAuditoria = false"
  />
</template>

<script setup>
import { ref, reactive, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'
import { useAuthStore } from '@/stores/auth'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)
const { obterClasseStatusGhost } = useUI()

const contabilistas = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const listas = reactive({ status: [] })

const filtros = reactive({ busca: '', lstStatus: '' })

const colunas = [
  { field: 'idContabilista', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'nome', label: 'Nome', sortable: true, class: 'fw-medium text-body-emphasis' },
  { field: 'cpf', label: 'CPF', width: 140, sortable: true },
  { field: 'crcNumero', label: 'CRC', width: 140, align: 'center', sortable: true, slotName: 'crc' },
  { field: 'cidade', label: 'Localidade', width: 180, sortable: false, slotName: 'localidade' },
  { field: 'lstStatus', label: 'Status', width: 120, align: 'center', sortable: true, slotName: 'status' },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (field !== 'lstStatus') return null
  return [...data].sort((a, b) => {
    const cmp = getStatusTexto(a.lstStatus).localeCompare(getStatusTexto(b.lstStatus), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => { buscarDados(1) }

const carregarListas = async () => {
  try {
    listas.status = await metadataStore.getListaLookup('ST', 'Cliente')
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const getStatusTexto = (idStatus) => {
  if (!idStatus) return '-'
  const obj = listas.status.find(s => s.idListaSistemaItem === idStatus || s.idListaClienteItem === idStatus)
  return obj ? obj.textoLista : idStatus
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    contabilistas.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/Contabilistas?${params.toString()}`)
    contabilistas.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error(error)
    erro.value = 'Erro ao carregar contabilistas.'
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => buscarDados(1)
const limparFiltros = () => { filtros.busca = ''; filtros.lstStatus = ''; buscarDados(1) }
const mudarPagina = (page) => buscarDados(page)
const mudarTamanhoPagina = (size) => { paginacao.PageSize = size; buscarDados(1) }

const abrirAuditoria = (row) => {
  auditoriaSelecionada.id = row.idContabilista
  auditoriaSelecionada.nome = row.nome
  mostrarAuditoria.value = true
}

watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) buscarDados(1)
  else contabilistas.value = []
})

onMounted(() => { carregarListas(); buscarDados(1) })
onActivated(() => buscarDados(paginacao.CurrentPage))

defineExpose({ buscarDados })
</script>
